// Provider detail route: plan list, signal history chart, sentiment over time, diffs this week.

import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, BarChart, Bar } from 'recharts';
import { t } from '../lib/i18n';
import type { Lang } from '../lib/i18n';
import { api } from '../lib/api';
import type { ProviderDetail } from '../lib/api';
import { VerifiedBadge } from '../components/VerifiedBadge';
import { formatUSD, formatGB, formatDays, formatDeltaPct, deltaClass } from '../lib/format';

interface ProviderProps {
  lang: Lang;
  currentWeek: number | null;
  code: string;
}

export function Provider({ lang, currentWeek, code }: ProviderProps) {
  const { id } = useParams<{ id: string }>();
  const [detail, setDetail] = useState<ProviderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);

    api.providers
      .detail(id, currentWeek ?? undefined)
      .then(setDetail)
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [id, currentWeek]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-neutral-500 text-sm">
        {t('loading', lang)}
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="flex items-center justify-center py-24 text-neutral-500 text-sm">
        {error ?? t('noData', lang)}
      </div>
    );
  }

  const { provider, plans, signals, diffs } = detail;

  // Build signal history chart data (grouped by week)
  const weeklySignals = signals.reduce<Record<number, number>>((acc, s) => {
    acc[s.snapshot_week] = (acc[s.snapshot_week] ?? 0) + s.mention_count;
    return acc;
  }, {});

  const trendData = Object.entries(weeklySignals)
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([week, mentions]) => ({
      week: `W${String(Number(week) % 100).padStart(2, '0')}`,
      mentions,
    }));

  // Sentiment over time
  const sentimentByWeek = signals.reduce<
    Record<number, { pos: number; neu: number; neg: number; total: number }>
  >((acc, s) => {
    if (!acc[s.snapshot_week]) acc[s.snapshot_week] = { pos: 0, neu: 0, neg: 0, total: 0 };
    const w = acc[s.snapshot_week];
    w.total += s.mention_count;
    w.pos += (s.sentiment_positive ?? 0) * s.mention_count;
    w.neu += (s.sentiment_neutral ?? 0) * s.mention_count;
    w.neg += (s.sentiment_negative ?? 0) * s.mention_count;
    return acc;
  }, {});

  const sentimentData = Object.entries(sentimentByWeek)
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([week, s]) => ({
      week: `W${String(Number(week) % 100).padStart(2, '0')}`,
      [t('positive', lang)]: s.total > 0 ? Math.round((s.pos / s.total) * 100) : 0,
      [t('neutral', lang)]: s.total > 0 ? Math.round((s.neu / s.total) * 100) : 0,
      [t('negative', lang)]: s.total > 0 ? Math.round((s.neg / s.total) * 100) : 0,
    }));

  const categoryLabel: Record<string, string> = {
    consumer_esim: t('consumer_esim', lang),
    corporate_iot: t('corporate_iot', lang),
    airline_wifi: t('airline_wifi', lang),
    // Legacy fallbacks
    established: t('consumer_esim', lang),
    new_entrant: t('consumer_esim', lang),
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-8">
      {/* Back link */}
      <Link to={`/?code=${code}`} className="text-xs text-neutral-500 hover:text-neutral-700 transition-colors">
        ← {t('radar', lang)}
      </Link>

      {/* Provider header */}
      <div className="card p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-xl font-semibold text-neutral-900">{provider.name}</h1>
            <p className="text-xs text-neutral-500 mt-1">{categoryLabel[provider.category] ?? provider.category}</p>
            {provider.description && (
              <p className="text-sm text-neutral-600 mt-3 leading-relaxed">{provider.description}</p>
            )}
          </div>
          <div className="flex flex-col gap-2 shrink-0">
            {provider.homepage_url && (
              provider.homepage_reachable === 0 ? (
                <span
                  title={t('sourceUnreachableHint', lang)}
                  className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-1.5"
                >
                  {t('homepage', lang)} · {t('sourceUnreachable', lang)}
                </span>
              ) : (
                <a
                  href={provider.homepage_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-neutral-600 hover:text-emerald-600 transition-colors border border-neutral-300 rounded px-3 py-1.5"
                >
                  {t('homepage', lang)} →
                </a>
              )
            )}
            {provider.pricing_url && (
              provider.pricing_reachable === 0 ? (
                <span
                  title={t('sourceUnreachableHint', lang)}
                  className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-1.5"
                >
                  {t('viewPlans', lang)} · {t('sourceUnreachable', lang)}
                </span>
              ) : (
                <a
                  href={provider.pricing_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-neutral-600 hover:text-emerald-600 transition-colors border border-neutral-300 rounded px-3 py-1.5"
                >
                  {t('viewPlans', lang)} →
                </a>
              )
            )}
          </div>
        </div>
      </div>

      {/* Plans table */}
      <section>
        <h2 className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-3">
          {t('plans', lang)}
        </h2>
        {plans.length === 0 ? (
          <div className="card p-6 text-neutral-500 text-sm">{t('noPlans', lang)}</div>
        ) : (
          <div className="card overflow-x-auto">
            <table className="data-table min-w-[700px]">
              <thead>
                <tr>
                  <th>{t('plan', lang)}</th>
                  <th>{t('region', lang)}</th>
                  <th>{t('data', lang)}</th>
                  <th>{t('validity', lang)}</th>
                  <th>{t('priceUSD', lang)}</th>
                  <th>{t('perGB', lang)}</th>
                  <th>{t('promo', lang)}</th>
                  <th>{t('verified', lang)}</th>
                </tr>
              </thead>
              <tbody>
                {plans.map((plan) => {
                  const ppgb = plan.data_gb && plan.price_usd ? plan.price_usd / plan.data_gb : null;
                  return (
                    <tr key={plan.id}>
                      <td className="text-xs max-w-[160px] truncate">{plan.plan_name ?? '-'}</td>
                      <td className="text-neutral-600">{plan.region ?? '-'}</td>
                      <td className="tabular-nums">
                        {plan.data_unlimited === 1
                          ? <span className="text-sky-600 text-xs">{t('unlimited', lang)}</span>
                          : formatGB(plan.data_gb)}
                      </td>
                      <td className="tabular-nums text-neutral-600">{formatDays(plan.validity_days)}</td>
                      <td className="tabular-nums text-neutral-800">{formatUSD(plan.price_usd)}</td>
                      <td className="tabular-nums text-emerald-600">{ppgb != null ? formatUSD(ppgb) : '-'}</td>
                      <td>
                        {plan.promo_price_usd != null ? (
                          <span className="text-amber-600 text-xs">{formatUSD(plan.promo_price_usd)}</span>
                        ) : '-'}
                      </td>
                      <td><VerifiedBadge verified={plan.is_verified === 1} lang={lang} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Charts row */}
      <div className="grid gap-4 sm:grid-cols-2">
        {/* Signal history */}
        <div className="card p-4">
          <div className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-3">
            {t('signalHistory', lang)}
          </div>
          {trendData.length === 0 ? (
            <div className="text-neutral-500 text-sm py-8 text-center">{t('noData', lang)}</div>
          ) : (
            <ResponsiveContainer width="100%" height={150}>
              <LineChart data={trendData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
                <XAxis dataKey="week" tick={{ fontSize: 10, fill: '#525252' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: '#525252' }} axisLine={false} tickLine={false} width={40} />
                <Tooltip contentStyle={{ background: '#171717', border: '1px solid #404040', borderRadius: 4, fontSize: 11 }} />
                <Line type="monotone" dataKey="mentions" stroke="#38bdf8" strokeWidth={1.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Sentiment over time */}
        <div className="card p-4">
          <div className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-3">
            {t('sentimentOverTime', lang)}
          </div>
          {sentimentData.length === 0 ? (
            <div className="text-neutral-500 text-sm py-8 text-center">{t('noData', lang)}</div>
          ) : (
            <ResponsiveContainer width="100%" height={150}>
              <BarChart data={sentimentData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
                <XAxis dataKey="week" tick={{ fontSize: 10, fill: '#525252' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: '#525252' }} axisLine={false} tickLine={false} width={40} />
                <Tooltip contentStyle={{ background: '#171717', border: '1px solid #404040', borderRadius: 4, fontSize: 11 }} />
                <Bar dataKey={t('positive', lang)} stackId="s" fill="#34d399" radius={[0, 0, 0, 0]} />
                <Bar dataKey={t('neutral', lang)} stackId="s" fill="#404040" />
                <Bar dataKey={t('negative', lang)} stackId="s" fill="#f43f5e" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Sources & quotes for the current week — core product-team ask: clicking
          any sentiment number on the dashboard must reveal which sources back it. */}
      {currentWeek && (() => {
        const currentSignals = signals.filter((s) => s.snapshot_week === currentWeek && s.mention_count > 0);
        if (currentSignals.length === 0) return null;
        return (
          <section>
            <h2 className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-3">
              {lang === 'tr' ? 'BU HAFTAKİ KAYNAKLAR VE YORUMLAR' : 'SOURCES & QUOTES THIS WEEK'}
            </h2>
            <div className="card p-4 space-y-3">
              {currentSignals.map((s, idx) => {
                let quotes: Array<{ text: string; url?: string; sentiment?: string; date?: string }> = [];
                try {
                  const parsed = JSON.parse(s.sample_quotes ?? '[]');
                  if (Array.isArray(parsed)) quotes = parsed.filter((q) => q && typeof q.text === 'string');
                } catch { /* ignore */ }
                return (
                  <div key={idx} className="border-l-2 border-neutral-200 pl-3">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="text-xs font-medium text-neutral-800 uppercase">{s.source}</span>
                      <span className="text-[10px] text-neutral-500">·</span>
                      <span className="text-[10px] text-neutral-500 tabular-nums">
                        {s.mention_count} {lang === 'tr' ? 'bahsedilme' : 'mentions'}
                      </span>
                      {(s.sentiment_positive !== null || s.sentiment_negative !== null) && (
                        <>
                          <span className="text-[10px] text-neutral-500">·</span>
                          <span className="text-[10px]">
                            <span className="text-emerald-600/80">{Math.round((s.sentiment_positive ?? 0) * 100)}%</span>
                            <span className="text-neutral-500"> / </span>
                            <span className="text-neutral-500">{Math.round((s.sentiment_neutral ?? 0) * 100)}%</span>
                            <span className="text-neutral-500"> / </span>
                            <span className="text-rose-600/80">{Math.round((s.sentiment_negative ?? 0) * 100)}%</span>
                          </span>
                        </>
                      )}
                    </div>
                    {quotes.length > 0 ? (
                      <ul className="space-y-1.5">
                        {quotes.slice(0, 6).map((q, qIdx) => (
                          <li key={qIdx} className="text-[11px] leading-snug flex items-start gap-1.5">
                            <span
                              className={`w-1 h-1 rounded-full mt-1.5 shrink-0 ${
                                q.sentiment === 'positive'
                                  ? 'bg-emerald-500/70'
                                  : q.sentiment === 'negative'
                                    ? 'bg-rose-500/70'
                                    : q.sentiment === 'neutral'
                                      ? 'bg-neutral-500/60'
                                      : 'bg-neutral-300'
                              }`}
                            />
                            <div className="flex-1 min-w-0">
                              <span className="text-neutral-700">“{q.text}”</span>
                              {(q.url || q.date) && (
                                <span className="text-[10px] text-neutral-500 ml-1.5">
                                  {q.date && <span>{q.date}</span>}
                                  {q.url && q.url.startsWith('http') && (
                                    <>
                                      {q.date && ' · '}
                                      <a
                                        href={q.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-emerald-600/80 hover:text-emerald-600 underline decoration-dotted"
                                      >
                                        {lang === 'tr' ? 'kaynak →' : 'source →'}
                                      </a>
                                    </>
                                  )}
                                </span>
                              )}
                            </div>
                          </li>
                        ))}
                        {quotes.length > 6 && (
                          <li className="text-[10px] text-neutral-500 pl-2.5">
                            +{quotes.length - 6} {lang === 'tr' ? 'daha' : 'more'}
                          </li>
                        )}
                      </ul>
                    ) : (
                      <p className="text-[10px] text-neutral-500 italic">
                        {lang === 'tr' ? 'Bu kaynakta yorum saklanmadı.' : 'No quotes captured for this source.'}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        );
      })()}

      {/* Diffs this week */}
      <section>
        <h2 className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-3">
          {t('diffsThisWeek', lang)}
        </h2>
        {diffs.length === 0 ? (
          <div className="card p-6 text-neutral-500 text-sm">{t('noDiffs', lang)}</div>
        ) : (
          <div className="card overflow-hidden">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('field', lang) ?? 'Alan'}</th>
                  <th>{t('previous', lang)}</th>
                  <th>{t('current', lang)}</th>
                  <th>{t('change', lang)}</th>
                </tr>
              </thead>
              <tbody>
                {diffs.map((diff) => (
                  <tr key={diff.id} className={diff.is_biggest_mover === 1 ? 'bg-amber-400/5' : ''}>
                    <td className="text-xs text-neutral-600">{diff.field}</td>
                    <td className="tabular-nums text-neutral-500 text-xs">{diff.prev_value ?? '-'}</td>
                    <td className="tabular-nums text-neutral-800 text-xs">{diff.new_value ?? '-'}</td>
                    <td className={`tabular-nums text-xs font-medium ${deltaClass(diff.delta_pct)}`}>
                      {formatDeltaPct(diff.delta_pct)}
                      {diff.is_biggest_mover === 1 && (
                        <span className="ml-1 text-amber-600 text-xs">★</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
